// Real QR codes for the public menu link (spec §5.7, prototype `qrMatrix/qrSvgString/png`). Generated in the
// browser with a bundled library (no CDN, no external service); the library is loaded only when a QR is shown.
// Same rendering as the prototype: error correction M, 4-module white quiet zone, ink #1B1430 on white.

export const QR_INK = "#1B1430";
export const QR_QUIET = 4;
export const QR_PNG_SIZES = [512, 1024, 2048] as const;
export type QrPngSize = (typeof QR_PNG_SIZES)[number];

/** Dark/light modules, without the quiet zone. */
export type QrMatrix = boolean[][];

/** The QR points at the canonical public link plus `?src=qr`, so scans can be told apart (Lot 5 statistics). */
export function qrTarget(publicUrl: string): string {
  const u = new URL(publicUrl);
  u.searchParams.set("src", "qr");
  return u.toString();
}

export async function makeQr(text: string): Promise<QrMatrix> {
  const { default: qrcode } = await import("qrcode-generator");
  const q = qrcode(0, "M");
  q.addData(text, "Byte");
  q.make();
  const n = q.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => q.isDark(r, c)));
}

/** SVG path of the dark modules, offset by the quiet zone; horizontal runs are merged to keep files small. */
export function qrPath(m: QrMatrix): string {
  let d = "";
  m.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      if (!row[c]) continue;
      let len = 1;
      while (row[c + len]) len++;
      d += `M${c + QR_QUIET} ${r + QR_QUIET}h${len}v1h-${len}z`;
      c += len - 1;
    }
  });
  return d;
}

/** Standalone SVG document (download, print shops): scalable, crisp edges, white background. */
export function qrSvg(m: QrMatrix): string {
  const n = m.length + 2 * QR_QUIET;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges"><rect width="${n}" height="${n}" fill="#FFFFFF"/><path d="${qrPath(m)}" fill="${QR_INK}"/></svg>`;
}

/**
 * Pixel bounds of module (r, c) in a square PNG of `px` pixels. Edges are rounded so neighbouring modules
 * meet exactly (no hairline gaps) even when `px` is not a multiple of the module count.
 */
export function moduleBox(px: number, n: number, r: number, c: number) {
  const total = n + 2 * QR_QUIET;
  const at = (k: number) => Math.round((k * px) / total);
  const x = at(c + QR_QUIET);
  const y = at(r + QR_QUIET);
  return { x, y, w: at(c + QR_QUIET + 1) - x, h: at(r + QR_QUIET + 1) - y };
}

export async function qrPng(m: QrMatrix, px: QrPngSize): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const g = canvas.getContext("2d");
  if (!g) throw new Error("Codul QR nu a putut fi desenat. Descarcă varianta SVG.");
  g.fillStyle = "#FFFFFF";
  g.fillRect(0, 0, px, px);
  g.fillStyle = QR_INK;
  m.forEach((row, r) => {
    row.forEach((dark, c) => {
      if (!dark) return;
      const b = moduleBox(px, m.length, r, c);
      g.fillRect(b.x, b.y, b.w, b.h);
    });
  });
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("Codul QR nu a putut fi desenat. Descarcă varianta SVG.");
  return blob;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
